import { EnabledFeatureIcon } from "@/components/atoms/icons/EnabledFeatureIcon";
import { DisabledFeatureIcon } from "@/components/atoms/icons/DisabledFeatureIcon";
import { SelectPlanContext } from "@/components/atoms/SelectPlan";
import { getFeatureExtList } from "@/libs/billing/feature";
import { color } from "@/styles/theme";
import { css } from "@emotion/react";
import { Font } from "@moneyforward/mfx_biz_react_components";
import { Service } from "@/generated/graphql_billing";
import { useContext } from "react";

/**
 * `Props` interface for the props of the `PlanFeaturesList` component.
 * @property {FeatureExt[]} features - An array of `PlanFeature` that the `PlanFeaturesList` component should display.
 */
interface Props {
  service: Service;
}

/**
 * A functional component that displays a list of plan features.
 * @param features - An array of features to be displayed.
 * @returns A JSX element representing the list of features.
 */
export const FeatureList = ({ service }: Props) => {
  const ctx = useContext(SelectPlanContext);

  // filter out features that should not be shown, this is features that are not available in any plan
  const features = getFeatureExtList(service).filter(
    (f) => ctx.showFeaturesFor[service.code][f.code]
  );

  return (
    <div css={listStyle}>
      {features.map((feature) => (
        <div key={feature.id} style={listItemStyle}>
          {feature.isEnabled ? (
            <EnabledFeatureIcon height={10} width={10} />
          ) : (
            <DisabledFeatureIcon height={10} width={10} />
          )}
          <div>
            <Font style={textStyle(feature.isEnabled)}>{feature.name}</Font>
          </div>
        </div>
      ))}
    </div>
  );
};

const listStyle = css({
  listStyleType: "none",
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  justifyItems: "start",
});

const listItemStyle = {
  margin: 0,
  display: "flex",
  gap: 2,
  alignItems: "center",
};

const textStyle = (isEnabled: boolean) => ({
  color: isEnabled ? color.black : color.gray600,
  fontSize: "1.0rem",
});
